import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

const EINGABE_STIL = cn(
  'w-full min-h-touch rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900',
  'placeholder:text-slate-400 transition-colors',
  'focus:border-marke-600 focus:ring-2 focus:ring-marke-200 focus:outline-none',
  'disabled:bg-slate-100 disabled:text-slate-500',
  'aria-[invalid=true]:border-red-500 aria-[invalid=true]:ring-red-200',
  'dunkel:border-slate-600 dunkel:bg-slate-800 dunkel:text-slate-100 dunkel:placeholder:text-slate-500',
);

interface HuelleProps {
  label?: string;
  hilfetext?: ReactNode;
  fehler?: string | null;
  pflicht?: boolean;
  id: string;
  children: ReactNode;
  className?: string;
}

/** Gemeinsame Hülle aus Beschriftung, Hilfetext und Fehlermeldung. */
export const Feldhuelle = ({
  label,
  hilfetext,
  fehler,
  pflicht,
  id,
  children,
  className,
}: HuelleProps) => (
  <div className={cn('w-full', className)}>
    {label && (
      <label htmlFor={id} className="feldbeschriftung">
        {label}
        {pflicht && (
          <span className="ml-1 text-red-600" aria-hidden="true">
            *
          </span>
        )}
        {pflicht && <span className="sr-only"> (Pflichtfeld)</span>}
      </label>
    )}
    {children}
    {fehler ? (
      <p id={`${id}-fehler`} role="alert" className="mt-1 text-sm text-red-600 dunkel:text-red-400">
        {fehler}
      </p>
    ) : (
      hilfetext && (
        <p id={`${id}-hilfe`} className="mt-1 text-sm text-slate-500 dunkel:text-slate-400">
          {hilfetext}
        </p>
      )
    )}
  </div>
);

export interface FeldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label?: string;
  hilfetext?: ReactNode;
  fehler?: string | null;
  pflicht?: boolean;
  id?: string;
  huelleClassName?: string;
}

export const Feld = forwardRef<HTMLInputElement, FeldProps>(function Feld(
  { label, hilfetext, fehler, pflicht, id, className, huelleClassName, ...rest },
  ref,
) {
  const erzeugteId = useId();
  const feldId = id ?? erzeugteId;

  return (
    <Feldhuelle
      label={label}
      hilfetext={hilfetext}
      fehler={fehler}
      pflicht={pflicht}
      id={feldId}
      className={huelleClassName}
    >
      <input
        ref={ref}
        id={feldId}
        required={pflicht}
        aria-invalid={fehler ? true : undefined}
        aria-describedby={fehler ? `${feldId}-fehler` : hilfetext ? `${feldId}-hilfe` : undefined}
        className={cn(EINGABE_STIL, className)}
        {...rest}
      />
    </Feldhuelle>
  );
});

export interface MehrzeiligProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> {
  label?: string;
  hilfetext?: ReactNode;
  fehler?: string | null;
  pflicht?: boolean;
  id?: string;
}

export const Mehrzeilig = forwardRef<HTMLTextAreaElement, MehrzeiligProps>(function Mehrzeilig(
  { label, hilfetext, fehler, pflicht, id, className, rows = 4, ...rest },
  ref,
) {
  const erzeugteId = useId();
  const feldId = id ?? erzeugteId;

  return (
    <Feldhuelle label={label} hilfetext={hilfetext} fehler={fehler} pflicht={pflicht} id={feldId}>
      <textarea
        ref={ref}
        id={feldId}
        rows={rows}
        required={pflicht}
        aria-invalid={fehler ? true : undefined}
        aria-describedby={fehler ? `${feldId}-fehler` : hilfetext ? `${feldId}-hilfe` : undefined}
        className={cn(EINGABE_STIL, 'resize-y', className)}
        {...rest}
      />
    </Feldhuelle>
  );
});

export interface AuswahlProps extends Omit<InputHTMLAttributes<HTMLSelectElement>, 'id' | 'type'> {
  label?: string;
  hilfetext?: ReactNode;
  fehler?: string | null;
  pflicht?: boolean;
  id?: string;
  optionen: readonly string[];
  leerText?: string;
}

export const Auswahl = forwardRef<HTMLSelectElement, AuswahlProps>(function Auswahl(
  {
    label,
    hilfetext,
    fehler,
    pflicht,
    id,
    className,
    optionen,
    leerText = 'Bitte wählen',
    ...rest
  },
  ref,
) {
  const erzeugteId = useId();
  const feldId = id ?? erzeugteId;

  return (
    <Feldhuelle label={label} hilfetext={hilfetext} fehler={fehler} pflicht={pflicht} id={feldId}>
      <select
        ref={ref}
        id={feldId}
        required={pflicht}
        aria-invalid={fehler ? true : undefined}
        aria-describedby={fehler ? `${feldId}-fehler` : hilfetext ? `${feldId}-hilfe` : undefined}
        className={cn(EINGABE_STIL, className)}
        {...rest}
      >
        <option value="">{leerText}</option>
        {optionen.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </Feldhuelle>
  );
});

export const EINGABE_KLASSEN = EINGABE_STIL;
